import { Component, signal, computed, Input, Output, EventEmitter, ViewChild, ElementRef, AfterViewInit, OnInit, OnDestroy, inject } from '@angular/core';
import { RouterLink, RouterLinkActive, Router, NavigationEnd } from '@angular/router';
import { filter, takeUntil, catchError, distinctUntilChanged } from 'rxjs/operators';
import { Subject, firstValueFrom } from 'rxjs';
import { Avatar } from 'primeng/avatar';
import { Popover, PopoverModule } from 'primeng/popover';
import { CommonModule } from '@angular/common';

import { Icon } from '../icon/icon';
import { CenterService, Center } from '@ui-nuvertos/shared';
import { TenantModuleService } from '@ui-nuvertos/shared';
import { BrandingService, TenantBranding } from '@ui-nuvertos/shared';
import { AuthService, CenterInfo } from '@ui-nuvertos/auth';
import { ToasterService } from '@ui-nuvertos/auth';

type SubItem = { 
  label: string; 
  route: string; 
  module_id: number; 
  sub_module_id: number;
  /**
   * Optional tenant-module key for future per-sub-module gating. Absence means
   * the sub-item inherits visibility from its parent (the current PR relies
   * entirely on parent-level gating; this field is reserved for later).
   */
  module_key?: string;
  children?: SubItem[];
  actions?: any;
};
type MenuItem = {
  key: string;
  label: string;
  icon: string; // emoji or icon class
  route?: string;
  module_id: number;
  /**
   * Tenant-module registry key. If defined and the key is disabled for the
   * current tenant (per TenantModuleService), the whole item + subtree is
   * hidden from the sidebar. Absence means always-visible (e.g. Dashboard).
   */
  module_key?: string;
  children?: SubItem[];
};

@Component({
  selector: 'lib-sidebar',
  standalone: true,
  templateUrl: './sidebar.html',
  styleUrls: ['./sidebar.scss'],
  imports: [RouterLink, RouterLinkActive, Avatar, Icon, CommonModule, PopoverModule],
})
export class SidebarComponent implements AfterViewInit, OnInit, OnDestroy {

  private router = inject(Router);
  private centerService = inject(CenterService);
  private authService = inject(AuthService);
  private toasterService = inject(ToasterService);
  private tenantModules = inject(TenantModuleService);
  private brandingService = inject(BrandingService);
  private destroy$ = new Subject<void>();

  expanded = signal(true);
  openKey = signal<string | null>(null);
  openSubKey = signal<string | null>(null);
  query = signal('');
  currentUrl = signal<string>('');
  private manuallyToggled = new Set<string>(); // Track manually toggled items

  // Center dropdown state
  centerDropdownOpen = signal(false);
  centerQuery = signal('');
  currentCenter = signal<CenterInfo | null>(null);
  centers = signal<Center[]>([]);
  loadingCenters = signal(false);
  switchingCenter = signal(false);
  loadingCurrentCenter = signal(false);
  private sidebarToggleTimeout?: ReturnType<typeof setTimeout>;

  /**
   * Brand mark shown in the sidebar head. Starts on the bundled HCAH asset and
   * is replaced by the tenant's own `logo_url` once `/public/branding` resolves
   * (same source the login page brands itself from). Tenants whose branding
   * carries no logo keep the bundled default.
   */
  logoSrc = signal('logo_hcah.png');

  // Signal to track current user for reactive filtering
  private currentUser = signal(this.authService.getCurrentUser());

  // Expose user role for template binding
  userRole = computed(() => this.currentUser()?.role || '');

  @Input() items: MenuItem[] = [];
  @Output() sidebarToggled = new EventEmitter<boolean>();
  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;
  @ViewChild('centerPopover') centerPopover?: Popover;
  @ViewChild('centerSearchInput') centerSearchInput?: ElementRef<HTMLInputElement>;

  /**
   * Filter items based on user role AND tenant module enablement.
   *
   * Two layers, applied in order:
   *   1. Role-based (legacy): non-Super Admins don't see 'assets', and the
   *      'settings' group is narrowed per role.
   *   2. Module-based (new): any MenuItem whose `module_key` is set but NOT
   *      enabled for the current tenant is dropped (whole subtree disappears).
   *      Items without `module_key` (e.g. Dashboard) are always shown.
   *
   * Computed reads `tenantModules.modules()` signal, so the sidebar re-filters
   * automatically once `TenantModuleService.init()` (APP_INITIALIZER) resolves
   * or when `refresh()` is later called.
   */
  private filteredItems = computed(() => {
    const user = this.currentUser();
    const userRole = user?.role;
    const isSuperAdmin = userRole === 'Super Admin';
    const isAdmin = userRole === 'Admin';

    // Layer 1: role-based filtering — narrows the `settings` group per role.
    //
    // The blanket `item.key !== 'assets'` drop that used to live here is gone.
    // It hid Assets from every non-Super-Admin regardless of tenant, which both
    // duplicated and overrode Layer 2: `asset` is a per-tenant module in
    // `nuvertos_global.tenant_modules`, so enablement is the intended control.
    // Visibility is now decided there — a tenant with `asset` disabled still
    // sees nothing, and the roles that actually do the asset work (Manager,
    // Viewer) can reach it for tenants where it is enabled.
    let items: MenuItem[];
    if (!isSuperAdmin && user !== null) {
      items = this.items
        .map(item => {
          if (item.key === 'settings' && item.children) {
            const allowedLabels = ['Masters', 'Account Settings'];
            if (isAdmin) {
              allowedLabels.push('Approval Workflows', 'Sequence Configuration');
            }
            return {
              ...item,
              children: item.children.filter(child => allowedLabels.includes(child.label)),
            };
          }
          return item;
        });
    } else {
      items = this.items;
    }

    // Layer 2: module-enablement filtering, walking 3 levels (top → sub →
    // leaf). Skipped when this Sidebar instance's TenantModuleService has no
    // data — that means we're being hosted by a shell that's already filtered
    // the items via its own SidebarConfigService (federation gives each remote
    // bundle its own TenantModuleService instance, and only the host's gets
    // initialized). Reading modules() registers reactive dependency for
    // re-filter when data becomes available in standalone (accounting) mode.
    //
    // Empty-children pruning: if a parent's `module_key` is enabled but every
    // one of its children gets filtered out, the parent itself disappears.
    // Same cascade at the sub-module level (drops settings.user-management
    // visually if all of users/roles/user-roles are off).
    const tenantModules = this.tenantModules.modules();
    if (!tenantModules.length) return items;

    const isEnabled = (key?: string) => !key || this.tenantModules.isEnabled(key);

    return items
      .filter((it) => isEnabled(it.module_key))
      .map((it) =>
        !it.children
          ? it
          : {
              ...it,
              children: it.children
                .filter((c) => isEnabled(c.module_key))
                .map((c) =>
                  !c.children
                    ? c
                    : {
                        ...c,
                        children: c.children.filter((sc) => isEnabled(sc.module_key)),
                      },
                )
                .filter((c) => !c.children || c.children.length > 0),
            },
      )
      .filter((it) => !it.children || it.children.length > 0);
  });

  filtered = computed(() => {
    const q = this.query().toLowerCase().trim();
    const baseItems = this.filteredItems(); // Use user-filtered items
    if (!q) return baseItems;
    return baseItems
      .map((item) => {
        if (!item.children)
          return item.label.toLowerCase().includes(q) ? item : null;
        const kids = item.children
          .map((c) => {
            if (!c.children) {
              return c.label.toLowerCase().includes(q) ? c : null;
            }
            const subKids = c.children.filter((sc) =>
              sc.label.toLowerCase().includes(q)
            );
            if (subKids.length) return { ...c, children: subKids };
            return c.label.toLowerCase().includes(q) ? c : null;
          })
          .filter(Boolean) as SubItem[];
        if (kids.length) return { ...item, children: kids };
        return item.label.toLowerCase().includes(q) ? item : null;
      })
      .filter(Boolean) as MenuItem[];
  });

  /**
   * Centres narrowed by the switcher's search box. Mirrors how `filtered`
   * narrows the nav by `query` -- case-insensitive substring, and an empty
   * box means "show everything".
   */
  filteredCenters = computed(() => {
    const q = this.centerQuery().toLowerCase().trim();
    const all = this.centers();
    if (!q) return all;
    return all.filter((c) => c.name.toLowerCase().includes(q));
  });

  ngOnInit() {
    // Set initial URL
    this.currentUrl.set(this.router.url);
    
    // Check current route on initialization
    this.checkAndExpandActiveRoutes();

    // Pull the tenant's brand mark. BrandingService caches + shares the call,
    // so this rides on the response the login page already fetched.
    this.brandingService.getBranding()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (branding: TenantBranding) => {
          if (branding.logo_url) {
            this.logoSrc.set(branding.logo_url);
          }
        },
        error: (err) => {
          console.error('Error fetching tenant branding:', err);
        }
      });

    // Subscribe to user session changes to update currentUser signal and load center
    this.authService.currentUser$
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (user) => {
          // Update currentUser signal for reactive filtering
          this.currentUser.set(user);
        },
        error: (err) => {
          console.error('Error in user session subscription:', err);
        }
      });

    // Subscribe to user session changes and load center when user with center_id is available
    this.authService.currentUser$
      .pipe(
        filter(user => user !== null && !!user.center_id), // Only proceed when user has center_id
        distinctUntilChanged((prev, curr) => 
          prev?.center_id === curr?.center_id && 
          prev?.id === curr?.id &&
          prev?.email === curr?.email
        ), // Prevent duplicate calls when user hasn't changed
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: () => {
          // User is already validated by filter, loadCurrentCenter() will get it via getCurrentUser()
          this.loadCurrentCenter().catch(() => {
            // Error already handled in loadCurrentCenter with toast
          });
        },
        error: (err) => {
          console.error('Error in user session subscription:', err);
        }
      });
    
    // Listen to route changes
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event: NavigationEnd) => {
        this.currentUrl.set(event.url);
        this.checkAndExpandActiveRoutes();
      });
  }

  ngAfterViewInit() {
    // Focus search input on initial load if expanded
    if (this.expanded()) {
      setTimeout(() => {
        if (this.searchInput?.nativeElement) {
          this.searchInput.nativeElement.focus();
        }
      }, 100);
    }
  }

  ngOnDestroy() {
    // Clear any pending timeouts
    if (this.sidebarToggleTimeout) {
      clearTimeout(this.sidebarToggleTimeout);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  checkAndExpandActiveRoutes() {
    const currentUrl = this.currentUrl() || this.router.url;
    
    // Find which parent items should be expanded based on current route
    this.items.forEach((item) => {
      if (item.children) {
        // Check if any child route matches current URL
        const hasActiveChild = this.hasActiveChild(item, currentUrl);
        if (hasActiveChild) {
          // Only auto-expand if not manually toggled
          // If manually toggled, respect the current state
          if (!this.manuallyToggled.has(item.key)) {
            this.openKey.set(item.key);
          }
          
          // Check nested children (like Masters -> Vendor Type)
          item.children.forEach((sub) => {
            if (sub.children) {
              const hasActiveSubChild = sub.children.some(subsub => 
                currentUrl.startsWith(subsub.route) || currentUrl === subsub.route
              );
              if (hasActiveSubChild) {
                const subKey = this.subIdFor(item.key, sub.label);
                if (!this.manuallyToggled.has(subKey)) {
                  this.openSubKey.set(subKey);
                }
              }
            }
          });
        } else {
          // If no active child, only close if not manually toggled
          // If manually toggled and closed, keep it closed
          if (!this.manuallyToggled.has(item.key)) {
            if (this.openKey() === item.key) {
              this.openKey.set(null);
            }
          }
        }
      }
    });
  }

  hasActiveChild(item: MenuItem, currentUrl: string): boolean {
    if (!item.children) return false;
    
    return item.children.some((child) => {
      // Check if child route matches
      if (currentUrl.startsWith(child.route) || currentUrl === child.route) {
        return true;
      }
      
      // Check nested children
      if (child.children) {
        return child.children.some((subChild) => 
          currentUrl.startsWith(subChild.route) || currentUrl === subChild.route
        );
      }
      
      return false;
    });
  }

  toggleSidebar() {
    this.expanded.update((v) => {
      const newValue = !v;
      this.sidebarToggled.emit(newValue);
      
      // Clear search query and close open submenus when sidebar is collapsed
      if (!newValue) {
        this.query.set('');
        this.openKey.set(null); // Close any open submenus
        this.openSubKey.set(null); // Close any open nested submenus
      }
      
      return newValue;
    });
  }
  handleItemClick(item: MenuItem, event: Event) {
    // Prevent navigation if item has children (user wants to toggle, not navigate)
    if (item.children) {
      event.preventDefault();
      event.stopPropagation();
      
      // If sidebar is collapsed and this item has children, expand the sidebar first
      if (!this.expanded()) {
        this.toggleSidebar();
        // Wait for the sidebar to expand, then toggle the group
        this.sidebarToggleTimeout = setTimeout(() => {
          if (!this.destroy$.closed) {
            this.toggleGroup(item.key);
          }
        }, 200); // Wait for the animation to complete
      } else {
        this.toggleGroup(item.key);
      }
    }
    // If no children, let the routerLink handle navigation
  }

  toggleGroup(key: string) {
    this.openKey.update((k) => {
      const newKey = k === key ? null : key;
      // Track manual toggle - mark as manually toggled regardless of state
      // This prevents auto-expansion from overriding user's manual action
      this.manuallyToggled.add(key);
      return newKey;
    });
  }

  toggleSubGroup(key: string) {
    this.openSubKey.update((k) => {
      const newKey = k === key ? null : key;
      // Track manual toggle - mark as manually toggled regardless of state
      this.manuallyToggled.add(key);
      return newKey;
    });
  }

  isSubOpen(key: string): boolean {
    return this.openSubKey() === key;
  }
  
  isItemActive(item: MenuItem): boolean {
    if (!item.children) return false;
    const currentUrl = this.currentUrl() || this.router.url;
    return this.hasActiveChild(item, currentUrl);
  }
  
  idFor(key: string) {
    return `panel-${key}`;
  }

  subIdFor(key: string, subKey: string) {
    return `subpanel-${key}-${subKey}`;
  }

  /**
   * Load current center information from IndexedDB or backend
   */
  async loadCurrentCenter(): Promise<void> {
    // Prevent concurrent calls
    if (this.loadingCurrentCenter()) {
      return;
    }

    this.loadingCurrentCenter.set(true);
    try {
      // Get center_id from the current user session (from token)
      const user = this.authService.getCurrentUser();
      if (!user?.center_id) {
        throw new Error('Center ID not found in user session. Please log in again.');
      }

      const centerId = user.center_id;
      
      // Check if stored center matches the token's center_id
      const storedCenter = await this.authService.getCenterInfo();
      if (storedCenter?.id === centerId) {
        // Stored center matches token, use it
        this.currentCenter.set(storedCenter);
        return;
      }
      
      // Load center details by ID from token
      const center = await firstValueFrom(
        this.centerService.getCenterById(centerId).pipe(
          catchError(error => {
            console.error('Error loading center by ID:', error);
            const errorMsg = error?.error?.error || error?.message || 'Unknown error';
            throw new Error(`Unable to load center information: ${errorMsg}`);
          })
        )
      );
      
      if (!center?.data) {
        throw new Error(`Center information not available for ID ${centerId}`);
      }

      const centerInfo: CenterInfo = {
        id: center.data.id,
        code: center.data.code || '',
        name: center.data.name || '',
        location: center.data.location,
        description: center.data.description,
        is_active: center.data.is_active ?? true
      };
      this.currentCenter.set(centerInfo);
      await this.authService.saveCenterInfo(centerInfo);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load current center information';
      this.toasterService.error('Error', errorMessage);
    } finally {
      this.loadingCurrentCenter.set(false);
    }
  }

  /**
   * Toggle center dropdown
   */
  toggleCenterDropdown(event: Event, popover: Popover): void {
    if (!this.centerDropdownOpen()) {
      this.loadCenters();
    }
    popover.toggle(event);
  }

  /**
   * Popover is open: mirror it onto `centerDropdownOpen` (which drives the
   * caret) and put the cursor in the search box so typing filters straight
   * away. Deferred a tick so the overlay content has rendered.
   */
  onCenterPopoverShow(): void {
    this.centerDropdownOpen.set(true);
    setTimeout(() => this.centerSearchInput?.nativeElement.focus(), 0);
  }

  /**
   * Popover is dismissed -- by outside click, Escape or a selection. Reset the
   * search so the next open starts from the full list.
   */
  onCenterPopoverHide(): void {
    this.centerDropdownOpen.set(false);
    this.centerQuery.set('');
  }

  /**
   * Load all accessible centers
   */
  async loadCenters(): Promise<void> {
    if (this.loadingCenters()) return;

    this.loadingCenters.set(true);
    try {
      const response = await firstValueFrom(
        this.centerService.getCenters(100, 0, false).pipe(
          catchError(error => {
            console.error('Error loading centers:', error);
            this.toasterService.error('Error', 'Failed to load centers');
            throw error;
          })
        )
      );

      if (response && response.data && response.data.centers) {
        const centersList = response.data.centers;
        this.centers.set(centersList);
        
        // Update current center if not set or if current center is not in the list
        const currentCenterId = this.currentCenter()?.id;
        const currentCenterInList = centersList.find(c => c.id === currentCenterId);
        
        if (!this.currentCenter() || !currentCenterInList) {
          // Use stored center if available and in list
          const storedCenter = await this.authService.getCenterInfo();
          const storedCenterInList = storedCenter ? centersList.find(c => c.id === storedCenter.id) : null;
          
          if (storedCenterInList) {
            this.currentCenter.set(storedCenter);
          }
        }
      }
    } catch (error) {
      console.error('Error loading centers:', error);
    } finally {
      this.loadingCenters.set(false);
    }
  }

  /**
   * Handle center selection
   */
  async onCenterSelect(center: Center): Promise<void> {
    if (this.switchingCenter() || center.id === this.currentCenter()?.id) {
      this.centerPopover?.hide();
      return;
    }

    this.switchingCenter.set(true);
    try {
      await firstValueFrom(
        this.centerService.switchCenter(center.id).pipe(
          catchError(error => {
            console.error('Error switching center:', error);
            const errorMessage = error?.error?.error || 'Failed to switch center';
            if (error.status === 403) {
              this.toasterService.error('Unauthorized', 'You do not have access to this center');
            } else {
              this.toasterService.error('Error', errorMessage);
            }
            throw error;
          })
        )
      );

      // Update stored center info
      const centerInfo: CenterInfo = {
        id: center.id,
        code: center.code,
        name: center.name,
        location: center.location,
        description: center.description,
        is_active: center.is_active
      };
      await this.authService.saveCenterInfo(centerInfo);
      this.currentCenter.set(centerInfo);

      // Close dropdown
      this.centerPopover?.hide();

      // Show success message
      this.toasterService.success('Success', 'Center switched successfully');

      // Reload user session and navigate after it completes
      try {
        await firstValueFrom(
          this.authService.loadUserSession().pipe(
            takeUntil(this.destroy$)
          )
        );
        // Navigate to dashboard after session is reloaded
        this.router.navigate(['/dashboard']);
      } catch (err) {
        console.error('Error reloading user session:', err);
        // Still navigate even if session reload fails
        this.router.navigate(['/dashboard']);
      }
    } catch {
      // Error already handled in catchError
    } finally {
      this.switchingCenter.set(false);
    }
  }

  /**
   * Check if a center is the current center
   */
  isCurrentCenter(center: Center): boolean {
    return center.id === this.currentCenter()?.id;
  }

}
